import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Can, Icon, useAuth, Input } from '@iq/react-components';

import InvitationList from './InvitationList';
import Heading from '../../../../Heading';
import AddInvitationModal from './AddInvitationModal';
import PermissionLegend from '../../../../PermissionLegend';
import SimpleModal from '../../../../SimpleModal';
import RoleSelect from '../../../../RoleSelect';
import { DropdownMenu, DropdownTrigger } from '../../../../DropdownMenu';
import { getUiSchema, getTenantAdminInvitationSchema } from '../schema';

import {
  requestMembers,
  isLoadingMembers,
  isLoadingInvitations,
  requestInvitations,
  createInvitations,
  updateInvitation,
  deleteInvitation,
  getInvitations,
  getMembers,
  getHasPermission,
} from '../../../../../bundles/auth';
import { requestUsers, getUsers, getUsersLoading } from '../../../../../bundles/ad';
import { getSites } from '../../../../../bundles/sites';
import { capitalize, mailRegex, validateEmail } from '../../../../../utils';

const cleanUpRoles = (formData) => {
  // We need to remove the "No sites available" because otherwise it's added as a site.
  // The "No sites available" option is needed because JSON schema forms require a value on enums, otherwise it breaks.

  const cleanedData = { ...formData };
  for (const role of Object.keys(cleanedData.roles)) {
    cleanedData.roles[role] = cleanedData.roles[role].filter(
      (item) => item !== 'No sites available'
    );
  }
  return cleanedData;
};

const InvitationsTab = () => {
  const dispatch = useDispatch();
  const { realm } = useAuth();
  const loadingMembers = useSelector(isLoadingMembers);
  const invitations = useSelector(getInvitations);
  const loadingInvitations = useSelector(isLoadingInvitations);
  const loadingUsers = useSelector(getUsersLoading);
  const tenantUsers = useSelector(getUsers);
  const sites = useSelector(getSites);

  const orgMembers = useSelector((state) => getMembers(state, `org/${realm}`));

  const [initialized, setInitialized] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [displayAddInvitationModal, setDisplayAddInvitationModal] = useState(false);
  const [displayRoleDescriptions, setDisplayRoleDescriptions] = useState(false);
  const [inviteFormData, setInviteFormData] = useState(null);
  const [createdEmails, setCreatedEmails] = useState([]);
  const [submitDisabled, setSubmitDisabled] = useState(true);

  const canWriteInvitations = useSelector((state) =>
    getHasPermission(state, 'invitations/Write', { org: realm })
  );

  useEffect(() => {
    dispatch(requestUsers());
    dispatch(requestInvitations(realm));
  }, []);

  useEffect(() => {
    if (!loadingMembers) {
      setInitialized(true);
    }
  }, [loadingMembers]);

  const validTenantUsers = useMemo(() => {
    const invitedUsers = new Set(invitations.map((invite) => invite.email));
    if (tenantUsers && invitations) {
      return tenantUsers.filter(
        (user) => user.email && user.enabled && !invitedUsers.has(user.email)
      );
    }
    return [];
  }, [tenantUsers, invitations]);

  const validateFields = (formData) => {
    // TODO: validate email isn't already attached to a member?
    // If user is already assigned to a site, Tenant admin can assign further permissions directly...
    const hasEmail = !!formData?.email?.length;
    let hasRole = false;

    if (hasEmail) {
      for (const roleList of Object.values(formData?.roles || {})) {
        if (roleList.length > 0) {
          hasRole = true;
        }
      }
    }

    setSubmitDisabled(!hasEmail || !hasRole);
  };

  useEffect(() => {
    validateFields(inviteFormData);
  }, [inviteFormData]);

  useEffect(() => {
    if (!loadingUsers && !initialized) {
      dispatch(requestMembers(undefined, false));
    }
  }, [loadingUsers, initialized]);

  const onInviteUser = useCallback(
    (formData) => {
      const updatedUserData = {
        ...formData,
        siteInfo: { ...formData.siteInfo },
      };

      for (const [key, siteIds] of Object.entries(updatedUserData.roles)) {
        if (Array.isArray(siteIds) && siteIds.length > 0) {
          updatedUserData.siteInfo[key] = [
            ...(updatedUserData.siteInfo[key] || []),
            ...siteIds.map((siteId) => ({
              siteId,
              siteName: sites.find((site) => site.id === siteId)?.name,
            })),
          ];
        }
      }

      const cleanedFormData = cleanUpRoles(updatedUserData);
      dispatch(createInvitations({ ...cleanedFormData, org: realm }));
      setDisplayAddInvitationModal(false);
      setInviteFormData(null);
    },
    [realm, createdEmails, sites]
  );

  const onEditInvitation = useCallback((invitation) => {
    setInviteFormData(invitation);
  }, []);

  const onUpdateInvitation = useCallback((formData, invitationId) => {
    const cleanedFormData = cleanUpRoles(formData);
    dispatch(updateInvitation(cleanedFormData, invitationId));
    setInviteFormData(null);
  }, []);

  const onChangeInvitation = useCallback((formData) => {
    validateFields(formData);
    setInviteFormData(formData);
  }, []);

  const onCloseInvitationModal = useCallback(() => {
    setDisplayAddInvitationModal(false);
    setInviteFormData(null);
  }, []);

  const onDeleteInvitation = useCallback((inviteId) => dispatch(deleteInvitation(inviteId)), []);

  const onMemberTypeChange = useCallback(
    (memberType, itemData) => {
      const selectedInvite = invitations.find((index) => index.id === itemData.id);
      let previousSiteRole;
      for (const [role, siteIds] of Object.entries(selectedInvite.roles)) {
        if (siteIds.includes(itemData.siteId)) {
          previousSiteRole = role;
        }
      }
      if (previousSiteRole !== memberType) {
        const siteIndexOfPreviousRole = selectedInvite.roles[previousSiteRole].indexOf(
          itemData.siteId
        );
        const postData = {
          ...selectedInvite,
          roles: {
            ...selectedInvite.roles,
            [previousSiteRole]: [
              ...selectedInvite.roles[previousSiteRole].slice(0, siteIndexOfPreviousRole),
              ...selectedInvite.roles[previousSiteRole].slice(siteIndexOfPreviousRole + 1),
            ],
            [memberType]: [...(selectedInvite.roles[memberType] || []), itemData.siteId],
          },
        };

        dispatch(updateInvitation(postData, itemData.id));
      }
    },
    [orgMembers, invitations]
  );

  const handleCreatable = useCallback(
    (value) => {
      const emails = value.match(mailRegex);

      if (emails) {
        const lowerCasedEmails = emails.map((addr) => addr.toLowerCase());

        setInviteFormData((previousInviteFormData) => ({
          ...previousInviteFormData,
          email: [...(previousInviteFormData.email || []), ...lowerCasedEmails],
        }));
        setCreatedEmails((previousCreateEmail) => [...previousCreateEmail, ...lowerCasedEmails]);
      }
    },
    [inviteFormData]
  );

  const buildMenuTrigger = useCallback(
    (currentRole) => (
      <DropdownTrigger
        label={currentRole === 'admin' ? 'Site manager' : capitalize(currentRole)}
        icon="he-down"
      />
    ),
    []
  );

  const buildMenuOptions = useCallback(
    (itemData) =>
      orgMembers
        ? orgMembers.map((m) => ({
            label: m.type,
            value: m.type,
            onSelect: () => onMemberTypeChange(m.type, itemData),
          }))
        : [],
    [orgMembers, onMemberTypeChange]
  );

  const schema = useMemo(() => {
    if (orgMembers && sites) {
      const rolesWithSites = orgMembers.map((member) => {
        const otherRolesSites = inviteFormData
          ? Object.entries(inviteFormData.roles).reduce((acc, [role, siteIds]) => {
              if (role !== member.type) {
                return [...acc, ...siteIds];
              }
              return acc;
            }, [])
          : [];
        const filteredSites = sites.filter((s) => !otherRolesSites.includes(s.id));

        return {
          ...member,
          siteNames: filteredSites.map((s) => s.name),
          siteIds: filteredSites.map((s) => s.id),
        };
      });

      return getTenantAdminInvitationSchema(
        rolesWithSites,
        validTenantUsers,
        createdEmails,
        handleCreatable,
        validateEmail
      );
    }
    return {};
  }, [orgMembers, sites, inviteFormData, validTenantUsers, createdEmails]);

  const updateUserSchema = useMemo(
    () => ({
      ...schema,
      properties: {
        ...schema.properties,
        email: {
          ...(schema.properties?.email || []),
          type: 'string',
          readonly: true,
        },
      },
    }),
    [schema]
  );

  const getPermissions = useCallback(
    (item, itemType) => {
      if (Object.keys(item?.roles || {}).length === 0) return null;

      const siteRoles = Object.entries(item.roles).reduce((acc, [role, connectedSites]) => {
        for (const siteId of connectedSites) {
          const connectedSite = sites.find((s) => s.id === siteId);
          if (connectedSite) {
            acc.push([
              connectedSite.name,
              role,
              {
                siteId,
                org: connectedSite.org,
                id: item.id,
              },
            ]);
          }
        }
        return acc;
      }, []);

      return siteRoles.map((siteRole, index) => {
        const key = `${siteRole[1]}-${index}`;
        const userContent = [
          '',
          '',
          '',
          <div
            className="role-selector"
            key={key}
          >
            <div>{siteRole[0]}</div>
            {canWriteInvitations ? (
              <DropdownMenu
                className="blocked-item"
                value={siteRole[1]}
                menuYPlacement="auto"
                menuShouldEscapeOverflow
                trigger={buildMenuTrigger(siteRole[1])}
                menuOptions={buildMenuOptions(siteRole[2], itemType)}
              />
            ) : (
              <div>{siteRole[1]}</div>
            )}
          </div>,
        ];
        if (itemType === 'invitation') {
          return userContent.slice(1);
        }
        return userContent;
      });
    },
    [sites, buildMenuTrigger, buildMenuOptions]
  );

  return (
    <>
      <Heading
        contentLeft={
          <>
            <Input
              type="text"
              onChange={(event) => setSearchText(event.target.value)}
              value={searchText}
              placeholder="Search by email"
              className="user-filter"
            />
            <RoleSelect
              id="role-filter"
              value={roleFilter}
              isMulti={false}
              onChange={setRoleFilter}
              realm={realm}
            />
          </>
        }
        contentRight={
          <Can
            permission="members/Write"
            scope={{ org: realm }}
          >
            <Button
              className="role-descriptions"
              onClick={() => setDisplayRoleDescriptions(true)}
              activity="secondary"
              design="text"
              icon={
                <div className="info-icon">
                  <Icon icon="he-info" />
                </div>
              }
            >
              Role descriptions
            </Button>
            <Button
              className="button-create"
              onClick={() => setDisplayAddInvitationModal(true)}
            >
              Invite user
            </Button>
            {displayAddInvitationModal && (
              <AddInvitationModal
                formData={inviteFormData}
                sites={sites}
                invitations={invitations}
                isUpdate={false}
                schema={schema}
                uiSchema={getUiSchema(true)}
                submitDisabled={submitDisabled}
                onSubmit={onInviteUser}
                onChange={onChangeInvitation}
                onCloseModal={onCloseInvitationModal}
              />
            )}
          </Can>
        }
      />
      <InvitationList
        org={realm}
        formData={inviteFormData}
        invitations={invitations}
        searchText={searchText}
        roleFilter={roleFilter}
        isLoading={loadingUsers || loadingInvitations}
        schema={updateUserSchema}
        uiSchema={getUiSchema()}
        onEdit={onEditInvitation}
        canEdit={canWriteInvitations}
        onUpdate={onUpdateInvitation}
        onChange={onChangeInvitation}
        onClose={onCloseInvitationModal}
        onDelete={onDeleteInvitation}
        getPermissions={getPermissions}
      />

      {displayRoleDescriptions && (
        <SimpleModal
          title="Role descriptions"
          className="role-permissions-modal"
          onClose={() => setDisplayRoleDescriptions(false)}
        >
          <PermissionLegend />
        </SimpleModal>
      )}
    </>
  );
};

export default InvitationsTab;
